import impliedVolatility from 'implied-volatility';
import blackScholes from 'black-scholes';
import greeks from 'greeks';
import { DateHelper } from './primptives-tool/date-tool';

export enum BlackScholesType {
	Call = 'call',
	Put = 'put'
}
export const Default_Volatility = 0.4;
export const Default_Risk_Free_Interest_Rate = 0.3;
export const Default_Expected_Cost = 0;
export interface BlackScholesCalculatorResult {
	price: number;
	delta: number;
	gamma: number;
	vega: number;
	theta: number;
	rho: number;
	impliedVolatility: number;
}

export function calculateBlackScholesAll(
	type: BlackScholesType,
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate,
	expectedCost: number = Default_Expected_Cost
): BlackScholesCalculatorResult {
	const price = calculateBlackScholesPrice(
		type,
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		volatility,
		riskFreeInterestRate
	);
	const delta = calculateBlackScholesDelta(
		type,
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		volatility,
		riskFreeInterestRate
	);
	const gamma = calculateBlackScholesGamma(
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		volatility,
		riskFreeInterestRate
	);

	const vega = calculateBlackScholesVega(
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		volatility,
		riskFreeInterestRate
	);
	const theta = calculateBlackScholesTheta(
		type,
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		volatility,
		riskFreeInterestRate
	);
	const rho = calculateBlackScholesRho(
		type,
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		volatility,
		riskFreeInterestRate
	);

	const impliedVolatility = calculateBlackScholesImpliedVolatility(
		type,
		baseIsinYesterdayPrice,
		strikePrice,
		dueDate,
		expectedCost,
		riskFreeInterestRate
	);
	return { price, delta, gamma, theta, vega, rho, impliedVolatility };
}

export function calculateBlackScholesPrice(
	type: BlackScholesType,
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);

	return blackScholes.blackScholes(
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		volatility,
		riskFreeInterestRate,
		type
	);
}

function calculateBlackScholesDelta(
	type: BlackScholesType,
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return greeks.getDelta(
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		volatility,
		riskFreeInterestRate,
		type
	);
}

function calculateBlackScholesGamma(
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return greeks.getGamma(
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		volatility,
		riskFreeInterestRate
	);
}

function calculateBlackScholesVega(
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return greeks.getVega(
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		volatility,
		riskFreeInterestRate
	);
}

function calculateBlackScholesTheta(
	type: BlackScholesType,
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return greeks.getTheta(
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		volatility,
		riskFreeInterestRate,
		type
	);
}

function calculateBlackScholesRho(
	type: BlackScholesType,
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	volatility: number = Default_Volatility,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return greeks.getRho(
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		volatility,
		riskFreeInterestRate,
		type
	);
}

function calculateBlackScholesImpliedVolatility(
	type: BlackScholesType,
	baseIsinYesterdayPrice: number,
	strikePrice: number,
	dueDate: Date,
	expectedCost: number,
	riskFreeInterestRate: number = Default_Risk_Free_Interest_Rate
) {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return impliedVolatility.getImpliedVolatility(
		expectedCost,
		baseIsinYesterdayPrice,
		strikePrice,
		DateHelper.daysBetweenTwoDates(now, dueDate) / 365,
		riskFreeInterestRate,
		type
	);
}
